import { computePriceState, normalizePriceNumbersForState, PriceState, MIN_REAL_PRICE } from "./priceState";
import type { AvailabilityStatus, SignupStatus } from "./offerStatusEnums";
import { sessionWindowFill } from "./sessionWindowRule";
import { buildOfferLocationData } from "./offerLocationData";
import { storedSessionAgeInputs } from "./storedSessionAgeInputs";
export { storedSessionAgeInputs };

export interface CoreLocationData {
  id: string;
  name: string;
  nameSubtitle?: string;
  address?: any;
  orgName?: string;
  searchResultExcerpt?: string;
  photos?: { path?: string; yOffset?: number }[];
  campExperiences?: any[];
  providedByOrg?: any;
  religiousAffiliationKeys?: string[];
  allergyKeys?: string[];
  visibility?: string;
  forceHide?: boolean;
  doesScholarships?: boolean;
  doesWaitlist?: boolean;
  coedKey?: string;
  descAboutTheOrg?: string;
  descLocation?: string;
  descTimingAndBooking?: string;
  supportForKeys?: string[];
}

export interface CoreVariationData {
  key: string;
  displayTitle?: string;
  description?: string;
  eligibilityNotes?: string;
  numDays?: number;
  priceRegular?: number;
  priceNote?: string;
  regularOfferIncrementKey?: string;
  regularOfferIncrementAlternatives?: { key: string; price: number; note?: string }[];
  /**
   * KB-4181 — what a missing price MEANS for this program's sessions: `"free"` (a human claim: it costs
   * nothing) or `"unknown"` (the camp publishes no price). Absent = derive as before. A real price always
   * wins over it; it only answers a session whose own prices are not a price.
   */
  priceState?: PriceState;
  colorScheme?: string;
  [key: string]: any;
}

export interface DecimalEnumItem { id: string; value: number; shortLabel?: string; longLabel?: string }

export interface CoreInput {
  location: CoreLocationData;
  variation: CoreVariationData;
  decimalEnums: Record<string, DecimalEnumItem>;
  form: {
    displayTitle?: string;
    description?: string;
    eligibilityNotes?: string;
    url?: string;
    regUrl?: string;
    availabilityStatus?: AvailabilityStatus;
    signupStatus?: SignupStatus;
    startDate: Date;
    endDate: Date;
    numDaysOverride?: number | null;
    myOfferIncrementKey?: string | null;
    priceOverride?: number | null;
    priceNoteOverride?: string | null;
    currentSalePrice?: number | null;
    /**
     * KB-2103 — the CURATED price state already on the offer doc. Only `"free"`
     * is honoured, and only when no positive price is present; everything else
     * is re-derived. Free is a human claim about the world, so it is read here
     * and never inferred.
     */
    priceState?: string | null;
    timingNoteOverride?: string | null;
    regEvents?: any[] | null;
    agesAreOverrides?: boolean;
    variationAgeOverrideInput?: any;
    /** Session-level tier overrides (offers/<id>.pricingTierPriceOverrides); used to compute priceTier2Calc/priceTier3Calc */
    pricingTierPriceOverrides?: { tier2?: number | null; tier3?: number | null } | null;
    /**
     * KB-1588 — observed per-session length/price, set ONLY by the camp-data adapter-native writer
     * (observedToOffers) + the parity gate. Length and price live on the SESSION, so when present
     * they win over the variation's representative numDays/priceRegular (the variation is a generic
     * shape). The admin/bulk create path never sets these → its behavior is unchanged.
     */
    observedNumDays?: number | null;
    /**
     * KB-2877 — the pipeline could not derive a service-day count and is WITHHOLDING it, as
     * distinct from simply not carrying one. Beats the variation fallback; a curator's
     * `numDaysOverride` still beats it.
     */
    numDaysUnknown?: boolean;
    observedPriceRegular?: number | null;
    observedPriceTier2?: number | null;
    observedPriceTier3?: number | null;
    /**
     * KB-1592 — observed per-session age (the age sibling of observedNumDays/observedPriceRegular).
     * Set ONLY by the camp-data adapter-native writer + parity gate; applied over the variation's
     * representative band when the variation allows allowSessionAgeOverride. The admin/bulk create
     * path never sets these → its behavior is unchanged.
     */
    observedMinAge?: number | null;
    observedMaxAge?: number | null;
    /**
     * KB-2107 — observed per-session GRADE KEYS + VALUES, set ONLY by the camp-data adapter-native
     * writer + parity gate. When a grade-banded source ("(Grades 4-6)") supplies these, the offer
     * renders its GRADES on the card (grade-primary) rather than the approximate age; the value
     * travels with the key so grade-based search reads the observed number, not a stale variation
     * one (KB-2095 whole-set discipline). The admin/bulk create path never sets these → its behavior
     * is unchanged (gated strictly on the key being present).
     */
    observedMinGradeKey?: string | null;
    observedMaxGradeKey?: string | null;
    observedMinGradeValue?: number | null;
    observedMaxGradeValue?: number | null;
    /** KB-4516 — a placeholder offer keeps its variation's timings as curated (no default window). */
    isPlaceholder?: boolean | null;
  };
}

export interface CoreOutput {
  // KB-2877 — the producer can now emit `null` (a WITHHELD service-day count), and `numDaysState`
  // is the companion that says why (data-model §1d). Widening this one field rather than the whole
  // Offer type: round 2's O5 correctly notes both tsconfigs set `strict: false`, so most readers
  // would not be told either way — which is exactly why the seams had to be found by review rather
  // than by the compiler, and why the type should at least stop lying here.
  numDaysCalc: number | null;
  numDaysState: "withheld" | null;
  priceCalc: number | null;
  pricePerDayCalc: number | null;
  /** Total-price histogram bin floor (KB-1412); null when priceCalc is null. */
  priceBucket: string | null;
  /** Per-day-price histogram bin floor (KB-1412); null when pricePerDayCalc is null. */
  pricePerDayBucket: string | null;
  /**
   * KB-2103 — normalised to agree with `priceState`: `null` for unknown, `0`
   * for free. Explicitly `null` rather than `undefined` so the value survives
   * as a real Firestore null without depending on the callers' undefined→null
   * sweep, and so the Algolia record simply has no `priceRegular` attribute.
   */
  priceRegular: number | null;
  /**
   * KB-2103 — priced (`null`) / `"free"` / `"unknown"`. Lands on the offer doc,
   * which IS the Algolia record. Definitive for DISPLAY; the search filters are
   * made correct by the normalised numbers above, not by this field.
   */
  priceState: PriceState | null;
  /** Effective tier-2 price: session override > alternative > variation default */
  priceTier2Calc: number | null;
  /** Effective tier-3 price: session override > alternative > variation default */
  priceTier3Calc: number | null;
  /**
   * KB-2782 AC-2 — non-null only when a resolved tier price was withheld because it was not
   * strictly below the resolved public price (priceCalc). A resolved tier that is >= priceCalc is
   * not a discount — it's the two independent resolution chains disagreeing about what a parent
   * should see — so it's nulled out of priceTier2Calc/priceTier3Calc above rather than emitted.
   * This field is the reason, so a refusal renders as something a caller can count and log instead
   * of nothing at all.
   */
  priceTierWithheld: {
    tier2?: { attempted: number; publicPrice: number };
    tier3?: { attempted: number; publicPrice: number };
  } | null;
  effectiveRegularOfferIncrementKey?: string | undefined;
  effectivePriceNote?: string;
  sanitizedVariationData: any;
  locationData: any;
}

// ── Price histogram bucketing (KB-1412) ──────────────────────────────────────
// Each published offer is stamped with the lower-bound floor of the bin its
// price falls into, so Algolia can return per-bin session COUNTS as a facet
// (`priceBucket` / `pricePerDayBucket`) that drives the admin price histogram.
// Bin widths are tunable here; the search client derives bar spacing from the
// returned floors (it does NOT hardcode these), so changing a width needs no
// client change. Offers with no price get a null bucket and are absent from the
// facet — matching the histogram's sparsity contract (no-price offers occupy no
// bin and so vanish from a narrowed range, but still match a cleared filter).
export const PRICE_BUCKET_BIN_SIZE = 50; // total-price histogram bin width ($)
export const PRICE_PER_DAY_BUCKET_BIN_SIZE = 10; // per-day histogram bin width ($)

/** Floor `price` to its bin's lower bound, returned as a string (Algolia facet
 *  values are strings). Returns null for null / non-finite / negative prices so
 *  no-price offers are excluded from the histogram facet. */
export function priceBucketFloor(
  price: number | null | undefined,
  binSize: number,
): string | null {
  if (price == null || !Number.isFinite(price) || price < 0) return null;
  return String(Math.floor(price / binSize) * binSize);
}

export function prepareOfferDataCore(input: CoreInput): CoreOutput {
  const { location, variation, decimalEnums, form } = input;

  // Derive numDays
  const startDate = form.startDate;
  const endDate = form.endDate;
  // numDaysCalc: override > observed per-session length (KB-1588) > variation.numDays > inclusive span.
  // The observed session's resolved length wins over the variation's representative numDays (length
  // lives on the SESSION, e.g. a July-4 short week is 4 days under a 5-day variation). The variation
  // value is the fallback when no observed length is present (admin/bulk create never sets it).
  // KB-2877 — the withhold, in lockstep with offerComputeCore.cjs's identical branch. These two are
  // the same rule on two paths (this one is the dashboard/publish path), and KB-2866 established
  // that fixing one alone is reverted by the next republish from the other. A curator's
  // numDaysOverride still outranks the withhold; the VARIATION DEFAULT does not, because
  // substituting a generic 28-day shape for an unknown 292-day programme renders $198.21/day —
  // worse than the $173.44 defect this ticket fixes.
  let numDaysCalc: number | null;
  if (form.numDaysOverride != null) {
    numDaysCalc = form.numDaysOverride;
  } else if (typeof form.observedNumDays === 'number' && Number.isFinite(form.observedNumDays) && form.observedNumDays >= 1) {
    numDaysCalc = form.observedNumDays as number;
  } else if (form.numDaysUnknown === true) {
    numDaysCalc = null;
  } else if (typeof variation.numDays === 'number') {
    numDaysCalc = variation.numDays as number;
  } else {
    const start = startDate.getTime();
    const end = endDate.getTime();
    const diffDays = Math.floor((end - start) / (1000 * 60 * 60 * 24));
    numDaysCalc = Math.max(1, diffDays + 1);
  }

  // KB-1588 — observed per-session price (authoritative; wins over variation/alternative defaults).
  const observedPriceRegular =
    typeof form.observedPriceRegular === 'number' && Number.isFinite(form.observedPriceRegular)
      ? form.observedPriceRegular
      : null;

  // Check alternative pricing by matching decimal enum value to numDaysCalc
  let appliedAlternative: { key: string; price: number; note?: string } | null = null;
  if (Array.isArray(variation.regularOfferIncrementAlternatives) && variation.regularOfferIncrementAlternatives.length > 0) {
    for (const alt of variation.regularOfferIncrementAlternatives) {
      const enumItem = decimalEnums[alt.key];
      if (enumItem && enumItem.value === numDaysCalc) {
        appliedAlternative = alt;
        break;
      }
    }
  }

  // KB-2866 — `variation.priceRegular` is the price for `variation.numDays` days, NOT for this
  // session. Without this, a one-day unit price lands verbatim on a multi-day session (sonshine's
  // `December 21-22, Teacher Work Day, Full Day` showed $60 for a $120 camp, and pricePerDayCalc
  // halved it again to $30/day). Scaling is licensed only when the unit IS a single day; a flat
  // multi-day rate carries no per-day arithmetic and must never be multiplied — sonshine's own
  // five-day week is $275, not 5 × $60.
  //
  // This mirrors the identical block in `scripts/camp-data/_reconcile/offerComputeCore.cjs`. The
  // two are separate producers of the same field — camp-data writes offers through the .cjs core,
  // the dashboard/publish path writes them through this one — so a fix applied to only one of them
  // is reverted the next time an offer is republished from the other side. Change both together.
  // ⚠️ Test the price with `typeof`, NOT `Number()`. `Number(null)` is 0 and `Number.isFinite(0)`
  // is true, so a coercing guard scales a PRICELESS variation to `0 * numDays === 0` — turning an
  // unknown price into a confident "$0 — free" on a collection where $0 and absent already cannot
  // be told apart (firestore-data-model §1c). The `> 0` bound is the writer's `isRealPrice` rule.
  // ⚠️ AND the span must be SHORTER THAN A WEEK — a daily rate times five is not a week's price,
  // vendors bundle (sonshine's week is $275 against 5 × $60; offer `0106e075…` sells five days at
  // $185 against the same $300). Multiplying there is the opposite defect. The bound matches the
  // iClassPro adapter's own `spanKindFor`: a day and a week are published price points, everything
  // between is `other` with no published price, which is where a daily rate is the only rate.
  const WEEK_DAYS = 5;
  // `typeof`, to match the `numDaysCalc` chain above — a coercing test would scale off a STRING
  // numDays that the length chain itself treats as absent.
  const hasPerDayUnit = typeof variation.numDays === 'number' && variation.numDays === 1;
  const unitPrice = variation.priceRegular;
  const hasRealUnitPrice = typeof unitPrice === 'number' && Number.isFinite(unitPrice) && unitPrice > 0;

  // ⚠️ `numDaysCalc` is an inclusive CALENDAR SPAN on the camp-data path (the iClassPro adapter
  // supplies no explicit numDays, so `inferObservedNumDays` parses dateRangeText), weekends
  // included. As a divisor for the display-only pricePerDayCalc an inflated span understates; as a
  // MULTIPLIER on the headline price it overcharges — a Fri+Mon pair spans 4 days and runs 2, so
  // $60/day would render $240 for a $120 camp. Only scale a span we can read as camp days.
  // ⚠️ The multiplier must BE the validated quantity. Checking "the span is all weekdays" and then
  // multiplying by `numDaysCalc` validates one number and uses another — a same-day span with
  // `observedNumDays: 3` would validate one Monday and charge three days. So the weekday span must
  // also EQUAL the count; a disagreement means the length did not come from the dates we can see.
  // ⚠️ LOCAL accessors here, UTC accessors in the .cjs twin — a DELIBERATE divergence, not a
  // copy error, and recorded as such in the parity pin (`censusUnitPriceMismatchLib.test.cjs`).
  // Each side reads its own producer's convention so both resolve the SAME intended calendar day:
  // camp-data's writer stamps `startDate`/`endDate` at NOON UTC, where `getUTC*` is exact, while
  // this path is reached from the dashboard, which builds LOCAL-MIDNIGHT Dates — reading those with
  // `getUTC*` lands on the previous day in every timezone ahead of UTC, so the span would resolve to
  // the wrong weekday and this producer would decline to scale a row camp-data scales. Unifying the
  // accessor is what would break it; matching each caller is what keeps the two answers equal.
  // (KB-2866 round-3 finding 5.)
  const weekdaySpanDays = ((): number | null => {
    if (!(startDate instanceof Date) || !(endDate instanceof Date)) return null;
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;
    const DAY_MS = 86400000;
    const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()).getTime();
    const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()).getTime();
    if (endDay < startDay) return null;
    if ((endDay - startDay) / DAY_MS > 366) return null;
    let days = 0;
    for (let t = startDay; t <= endDay; t += DAY_MS) {
      const dow = new Date(t).getDay();
      if (dow === 0 || dow === 6) return null;
      days += 1;
    }
    return days;
  })();
  const spanIsAllWeekdays = weekdaySpanDays != null && weekdaySpanDays === numDaysCalc;

  const unitPriceScaled =
    !appliedAlternative &&
    hasPerDayUnit &&
    hasRealUnitPrice &&
    Number.isFinite(numDaysCalc) &&
    numDaysCalc > 1 &&
    numDaysCalc < WEEK_DAYS &&
    spanIsAllWeekdays
      ? unitPrice * numDaysCalc
      : null;

  // `priceRegular` and `priceRegularTier2/3` are ONE PAIR IN ONE UNIT — the list price and its
  // member counterparts. A sale or override changes what is CHARGED (`priceCalc`), not the list, so
  // neither decouples the pair: whenever the unit price is scaled its tiers scale with it,
  // unconditionally. Gating this on the override chain (round-1 finding 7, since reversed) scaled
  // the public half and left the member half at the one-day rate, rendering "$120 / members $50" on
  // a tiered two-day camp whose member price is $100 — and for a tiered variation the guard's anchor
  // is `priceRegular`, so 50 < 120 passes and the phantom discount renders.
  const unitPriceScaleFactor = unitPriceScaled == null ? null : numDaysCalc;

  let priceRegular = unitPriceScaled ?? variation.priceRegular;
  let effectivePriceNote = "";
  let effectiveRegularOfferIncrementKey = variation.regularOfferIncrementKey;
  if (appliedAlternative) {
    priceRegular = appliedAlternative.price;
    effectivePriceNote = appliedAlternative.note || "";
    effectiveRegularOfferIncrementKey = appliedAlternative.key;
  }
  // KB-1588: the observed session price is this offer's REGULAR price (not a sale) — it overrides
  // the variation/alternative default for both priceRegular and priceCalc below.
  if (observedPriceRegular != null) {
    priceRegular = observedPriceRegular;
  }

  // priceCalc priority: currentSalePrice > priceOverride > observed session price (KB-1588) > alternative > variation.priceRegular
  const rawPriceCalc = form.currentSalePrice != null
    ? form.currentSalePrice
    : form.priceOverride != null
      ? form.priceOverride
      : observedPriceRegular != null
        ? observedPriceRegular
        : appliedAlternative
          ? appliedAlternative.price
          : (unitPriceScaled ?? variation.priceRegular ?? null);

  // Tier-2/tier-3 effective price: session override > observed per-session tier (KB-1588) > alternative > variation default (same idea as priceCalc)
  // Computed BEFORE priceState normalization below (moved up from its old position after
  // bucketing, KB-2782) so both the state-based null-out (AC-5) and the ascending-tier withhold
  // (AC-1) run through the SAME normalizePriceNumbersForState call as priceCalc/priceRegular,
  // rather than a second, easy-to-forget pass over fields that look like they're already handled.
  const tierOverrides = (form as any).pricingTierPriceOverrides;
  const observedTier2 =
    typeof form.observedPriceTier2 === 'number' && Number.isFinite(form.observedPriceTier2) ? form.observedPriceTier2 : null;
  const observedTier3 =
    typeof form.observedPriceTier3 === 'number' && Number.isFinite(form.observedPriceTier3) ? form.observedPriceTier3 : null;
  // KB-2866 — a tier taken from the VARIATION default is in the same per-day unit the public price
  // was scaled out of, so it carries the same factor. Only that arm: an observed per-session tier
  // and an alternative's tier are already stated for THIS session's length.
  const scaleVariationTier = (tier: number | undefined): number | undefined =>
    unitPriceScaleFactor != null && typeof tier === 'number' && Number.isFinite(tier) && tier > 0
      ? tier * unitPriceScaleFactor
      : tier;
  const defaultTier2 = observedTier2 ?? (appliedAlternative as any)?.priceTier2 ?? scaleVariationTier(variation.priceRegularTier2);
  const defaultTier3 = observedTier3 ?? (appliedAlternative as any)?.priceTier3 ?? scaleVariationTier(variation.priceRegularTier3);
  const rawPriceTier2Calc = (tierOverrides?.tier2 != null ? tierOverrides.tier2 : defaultTier2) ?? null;
  const rawPriceTier3Calc = (tierOverrides?.tier3 != null ? tierOverrides.tier3 : defaultTier3) ?? null;

  // KB-2103 — derived from the SAME resolved prices the display reads, so the
  // state can never disagree with the number sitting next to it.
  //
  // KB-4181 — the curated input is the PROGRAM's declared state when it has one, else the offer's stored
  // one. Program first, because an offer's stored "free" may itself have been inherited from the program:
  // were the stored value to win, a program changed from Free to Price unknown would leave every session it
  // ever made free. A program with no declaration leaves the stored curation (the KB-2103 list) in charge.
  // `computePriceState` still lets a real price beat either. Mirrored in the pipeline's
  // `offerComputeCore.cjs` (`resolveProgramDefaultPrice`); `programPriceStateParity.test.ts` runs both.
  const programPriceState: PriceState | null =
    variation.priceState === "free" || variation.priceState === "unknown" ? variation.priceState : null;
  const priceState = computePriceState({
    priceCalc: rawPriceCalc,
    priceRegular,
    currentSalePrice: form.currentSalePrice,
    priceState: programPriceState ?? form.priceState,
  });

  // KB-2103 — and then make the numbers agree with the state, BEFORE the
  // per-day price and the histogram buckets are derived from them, so every
  // price-shaped field an unknown offer exposes to Algolia is null (and so
  // absent from the record, and so invisible to numeric filters). Deriving
  // first and normalising after would leave a pricePerDayCalc of 0 behind,
  // which is exactly the false "cheapest" match this ticket exists to remove.
  //
  // KB-2782 AC-5 — priceTier2Calc/priceTier3Calc now pass through the SAME call: an unknown/free
  // offer's tier is null too, not just its public price. Previously these two fields were computed
  // AFTER this normalization and never touched it, so KB-2103's stated guarantee — "every
  // price-shaped field an unknown offer exposes to Algolia is null" — was false for the tier
  // fields. Measured population today is 0 (no unknown/free offer currently carries a tier), so
  // this closes a latent gap rather than changing any live record.
  const normalized = normalizePriceNumbersForState(priceState, {
    priceCalc: rawPriceCalc,
    priceRegular: priceRegular ?? null,
    priceTier2Calc: rawPriceTier2Calc,
    priceTier3Calc: rawPriceTier3Calc,
  });
  const priceCalc = normalized.priceCalc;
  const priceRegularNormalized = normalized.priceRegular;

  const pricePerDayCalc = numDaysCalc > 0 && priceCalc != null ? priceCalc / numDaysCalc : null;

  // KB-1412 — histogram bin floors for the price filter's two axes.
  const priceBucket = priceBucketFloor(priceCalc, PRICE_BUCKET_BIN_SIZE);
  const pricePerDayBucket = priceBucketFloor(pricePerDayCalc, PRICE_PER_DAY_BUCKET_BIN_SIZE);

  // KB-2782 AC-1 — the two chains above (public price via priceCalc, tier price via the
  // resolution above) run independently and NOTHING compared them before this. A resolved tier
  // that is not STRICTLY BELOW the resolved public price is not a discount — it's the two chains
  // disagreeing about what a parent should see, and the higher number reads as a special/member
  // rate. Withhold (null), never recompute: we do not know the session's real member price, and
  // inventing one is the same error pointed the other way.
  //
  // "The resolved public price" is NOT ONE FIELD — it's whichever field is ACTUALLY rendered
  // beside the tier, and that depends on whether the variation declares tiered pricing at all.
  //
  // Verified directly against marketing's rendering (kidsbooked-marketing `src/lib/camp-data.ts`
  // `projectPriceTiers` + `SessionPage.tsx`): when `variation.pricingTiers` declares numTiers 2|3,
  // the tier card's "Tier 1" is `offer.priceRegular` UNCONDITIONALLY — the sale/override-aware
  // branch (`currentSalePrice`, and by extension `priceCalc`) is gated `!tiers` and NEVER reaches a
  // parent for a tiered offer. kb-ff's Flutter app renders no tier UI at all, so when pricingTiers
  // is NOT declared, priceTier2Calc/3Calc are never customer-facing — only the admin dashboard
  // shows them, where priceCalc (this codebase's general "effective price") is the right anchor.
  //
  // This was found by re-deriving AC-6's denominators against real prod data (round 2 of this
  // ticket's own review): 5 of the 22 already-repaired offers ("TO CARE Priority Registration")
  // DO declare pricingTiers — priceRegular 276 / tier2 184 pre-repair, a real ~33% discount a
  // parent actually saw, not the "6.1x" the brief's priceCalc framing implied (priceCalc:30 there
  // is a priceOverride on what KB-2698 has independently flagged as a mis-modeled registration-fee
  // product, and never reaches the tier card). The other 17 repaired rows are unaffected either
  // way: durham's `observedPriceRegular` sets priceRegular AND priceCalc identically (no
  // divergence), and the 5 Camp Kerr/Great Adventure rows declare no pricingTiers at all.
  //
  // The effective-price fallback below (never priceCalc alone) is for the NOT-tiered case only —
  // priceCalc can itself be sub-threshold junk (e.g. a stray $5) while priceRegular carries the
  // real $300 kb-ff's `PriceDisplayHelper._effectivePrice` falls back to; comparing against
  // priceCalc alone there would withhold a legitimate tier keyed to the $300.
  //
  // Only two rungs, not three: `_effectivePrice` also checks currentSalePrice, but that rung is
  // unreachable HERE (round 2 review, confirmed) — priceCalc's own resolution chain above already
  // puts currentSalePrice FIRST (`form.currentSalePrice != null ? form.currentSalePrice : ...`), so
  // whenever currentSalePrice is real, priceCalc already equals it and the priceCalc rung catches
  // it. Unlike kb-ff, which reads three independently-stored Firestore fields that CAN diverge on
  // the read side, this function DERIVES priceCalc from currentSalePrice in the same computation —
  // they cannot diverge here.
  const pricingTiersDeclared = (() => {
    const pt = (variation as any)?.pricingTiers;
    return pt != null && typeof pt === "object" && (pt.numTiers === 2 || pt.numTiers === 3);
  })();
  // KB-2782 round 3 — the tiered branch's floor is `> 0`, not `>= MIN_REAL_PRICE`: it must match
  // marketing's OWN gate (`projectPriceTiers`'s `tier1Price > 0`) exactly, a different threshold
  // concept from the "junk/Ask-provider" MIN_REAL_PRICE used below. Without this, a variation whose
  // priceRegular resolves to literal `0` (documented reachable — firestore-data-model.md §1c: 37 of
  // 65 no-price offers store 0/0, inherited from an incomplete catalog default) while priceCalc is
  // independently real (an admin priceOverride) would set effectivePublicPrice to `0` rather than
  // null, and then EVERY positive tier value is trivially `>= 0` — withholding real tiers
  // marketing's own gate would still render (it skips the row only when tier1Price <= 0).
  const effectivePublicPrice = pricingTiersDeclared
    ? priceRegularNormalized != null && priceRegularNormalized > 0
      ? priceRegularNormalized
      : null
    : priceCalc != null && priceCalc >= MIN_REAL_PRICE
      ? priceCalc
      : priceRegularNormalized != null && priceRegularNormalized >= MIN_REAL_PRICE
        ? priceRegularNormalized
        : null;
  const tier2Ascending =
    normalized.priceTier2Calc != null && effectivePublicPrice != null && normalized.priceTier2Calc >= effectivePublicPrice;
  const tier3Ascending =
    normalized.priceTier3Calc != null && effectivePublicPrice != null && normalized.priceTier3Calc >= effectivePublicPrice;
  const priceTier2Calc = tier2Ascending ? null : normalized.priceTier2Calc;
  const priceTier3Calc = tier3Ascending ? null : normalized.priceTier3Calc;
  // AC-2 — count and report the withholding; a refusal that renders as nothing is the defect
  // class this exists to close. Present only when at least one tier was actually withheld.
  const priceTierWithheld: CoreOutput["priceTierWithheld"] =
    tier2Ascending || tier3Ascending
      ? {
          ...(tier2Ascending
            ? { tier2: { attempted: normalized.priceTier2Calc as number, publicPrice: effectivePublicPrice as number } }
            : {}),
          ...(tier3Ascending
            ? { tier3: { attempted: normalized.priceTier3Calc as number, publicPrice: effectivePublicPrice as number } }
            : {}),
        }
      : null;

  // Sanitize variationData and inject effective pricing
  const { colorScheme, ...sanitized } = variation;
  const sanitizedVariationData = {
    ...sanitized,
    ...(appliedAlternative && {
      regularOfferIncrementKey: effectiveRegularOfferIncrementKey,
      priceRegular: appliedAlternative.price,
      priceNote: effectivePriceNote
    })
  };

  // KB-1592 — observed per-session age wins over the variation's representative band, but ONLY when
  // the matched variation allows it (allowSessionAgeOverride). The variation band can be a broad UNION
  // over several session age groups; the true band lives on the SESSION (age sibling of KB-1588). Age
  // lives in variationData as minAgeInYears/maxAgeInYears (+ the *Effective mirror the offer reads) —
  // override both, matching offerDataPreparationServer.ts's age-override merge. null observed age OR
  // override-not-allowed → the variation band stands (admin/bulk create never sets observed age).
  const observedMinAge =
    typeof form.observedMinAge === 'number' && Number.isFinite(form.observedMinAge) ? form.observedMinAge : null;
  const observedMaxAge =
    typeof form.observedMaxAge === 'number' && Number.isFinite(form.observedMaxAge) ? form.observedMaxAge : null;
  if ((variation as any).allowSessionAgeOverride) {
    if (observedMinAge != null) {
      (sanitizedVariationData as any).minAgeInYears = observedMinAge;
      (sanitizedVariationData as any).minAgeEffective = observedMinAge;
    }
    if (observedMaxAge != null) {
      (sanitizedVariationData as any).maxAgeInYears = observedMaxAge;
      (sanitizedVariationData as any).maxAgeEffective = observedMaxAge;
    }
  }

  // KB-2107 — grade-primary display, AUTO-DERIVED from the source band (the .cjs twin of the
  // offerComputeCore block). A grade-banded source ("(Grades 4-6)") renders its GRADES on the card,
  // not the approximate age stamped into minAge/maxAgeInYears above. The card shows grades only when
  // showMin/MaxAgeGradeAsPrimary is true AND a min/maxAgeGradeKey exists, else falls through to age.
  // The observed grade band is mapped to a gradeKey (+ carries its numeric value) upstream
  // (observedToOffersLib → gradeValueToKey); when a key is present we stamp the FULL {key, value,
  // flag} triple — a key without its value leaves grade-based search reading the stale variation
  // number (KB-2095 whole-set discipline). Age stays as the SECONDARY value. Gated STRICTLY on the
  // observed grade key being present so admin/bulk create (never sets it) is byte-identical.
  const observedMinGradeKey =
    typeof form.observedMinGradeKey === 'string' && form.observedMinGradeKey ? form.observedMinGradeKey : null;
  const observedMaxGradeKey =
    typeof form.observedMaxGradeKey === 'string' && form.observedMaxGradeKey ? form.observedMaxGradeKey : null;
  const observedMinGradeValue =
    typeof form.observedMinGradeValue === 'number' && Number.isFinite(form.observedMinGradeValue) ? form.observedMinGradeValue : null;
  const observedMaxGradeValue =
    typeof form.observedMaxGradeValue === 'number' && Number.isFinite(form.observedMaxGradeValue) ? form.observedMaxGradeValue : null;
  if (observedMinGradeKey != null) {
    (sanitizedVariationData as any).minAgeGradeKey = observedMinGradeKey;
    (sanitizedVariationData as any).minAgeGradeValue = observedMinGradeValue;
    (sanitizedVariationData as any).showMinAgeGradeAsPrimary = true;
  }
  if (observedMaxGradeKey != null) {
    (sanitizedVariationData as any).maxAgeGradeKey = observedMaxGradeKey;
    (sanitizedVariationData as any).maxAgeGradeValue = observedMaxGradeValue;
    (sanitizedVariationData as any).showMaxAgeGradeAsPrimary = true;
  }

  // KB-4516 — the default drop-off/pick-up window (KB-3613), the same rule under the same gate as the .cjs
  // twin's block in offerComputeCore: fill-only, per group, never over a set value (null counts as unset);
  // a placeholder keeps its timings as curated. This is the PROGRAM's window: on a rewrite of an EXISTING
  // offer the seams then put back the groups the stored offer holds, by the rule in `storedSessionWindow.ts`
  // (KB-4564, `applyStoredSessionWindow`).
  const vdTimings = (sanitizedVariationData as any).timings;
  if (form.isPlaceholder !== true && vdTimings && typeof vdTimings === "object") {
    const windowFill = sessionWindowFill(vdTimings);
    if (Object.keys(windowFill).length > 0) (sanitizedVariationData as any).timings = { ...vdTimings, ...windowFill };
  }

  // KB-2145 — this file deliberately does NOT compute the offer's `observedBands` (the source's
  // own band statement); only the .cjs pipeline twin
  // (scripts/camp-data/_reconcile/offerComputeCore.cjs) does. An earlier draft mirrored it here
  // for "cross-language parity" and the result was ~40 lines of unreachable code: neither
  // `prepareOfferDataServer` nor the web `prepareOfferData` passes the band/age/grade `observed*`
  // inputs in, and neither forwards `observedBands` out into the payload it writes. Both build an
  // explicit `form` literal, so the compute could never fire. (Since KB-3733 both DO pass the
  // price/length/tier `observed*` inputs for a camp-data offer, via `campDataCatalogInputs` — read
  // from the stored doc so a rebuild keeps the session's own price — and since KB-4319 the age/grade
  // band inputs, via `storedSessionAgeInputs` (its own module, ./storedSessionAgeInputs.ts), valued from
  // the stored `variationData`. That module reads `observedBands` on the stored doc as provenance only
  // and returns only observed* inputs; neither seam passes the field in or forwards it out through this
  // literal, which is what this note relies on.)
  //
  // Wiring it through would be actively harmful, not merely redundant. This preparer's output is
  // the FULL intended top-level map for five paths that rewrite EXISTING offers
  // (publishOffersForLocation, syncSharedProgramsToLocationsAndOffers, applyCollisionProgramUpdates,
  // offerBatchUpdateService, and every single-offer admin save in offerCrud). The moment
  // `observedBands` enters this literal, each of those becomes an erasure channel for a field only
  // the pipeline may write. The clone path (offerCopyService → createSafeOfferCopy) is load-bearing
  // for the same reason in the other direction: it deep-copies the source offer INCLUDING
  // `observedBands`, and only this preparer's whitelist discards it — adding the field here would
  // make cloning silently copy another session's source statement.
  //
  // KB-4619 — the one shared stamp rule; the debounced location restamp calls the same builder.
  const locationData = buildOfferLocationData(location);

  return {
    numDaysCalc,
    // KB-2877 (review round 2, O4) — emitted here so every consumer of `core` gets the state
    // beside the value, exactly as the .cjs twin does. Without it the admin path could null a
    // count but never record WHY, and the next publish rebuilt the offer at the variation default.
    numDaysState: form.numDaysUnknown === true && numDaysCalc == null ? "withheld" : null,
    priceCalc,
    pricePerDayCalc,
    priceBucket,
    pricePerDayBucket,
    priceRegular: priceRegularNormalized,
    priceState,
    priceTier2Calc,
    priceTier3Calc,
    priceTierWithheld,
    effectiveRegularOfferIncrementKey,
    effectivePriceNote,
    sanitizedVariationData,
    locationData
  };
}

const AGE_UI_SENTINEL_KEYS = ["showMinAgeGradeAsPrimary", "showMaxAgeGradeAsPrimary", "minAgeNote", "maxAgeNote"] as const;

/**
 * KB-4319 — the session form's age-UI defaults (`normalize`, i.e. `normalizeVariationDataAgeUiSentinels`) on a rebuilt
 * `variationData`. On a camp-data offer (`campDataStored`, the stored offer, given) no default is ADDED for a key that
 * neither the rebuild nor the stored `variationData` holds: the pipeline writer never fills these four, so adding them
 * made every Publish a `variationData` change the nightly proposed taking back off (oak-city, apex, YWI). A key either
 * one holds is normalized as before. `campDataStored: null` (a curated offer, a create) normalizes all four, as before.
 */
export function normalizeAgeUiSentinelsKeepingStoredShape(
  vd: Record<string, unknown>,
  normalize: (v: Record<string, unknown>) => void,
  campDataStored: Record<string, unknown> | null,
): void {
  const stored = campDataStored?.variationData && typeof campDataStored.variationData === "object" ? (campDataStored.variationData as Record<string, unknown>) : {};
  const notAdded = campDataStored ? AGE_UI_SENTINEL_KEYS.filter((k) => !(k in vd) && !(k in stored)) : [];
  normalize(vd);
  for (const k of notAdded) delete vd[k];
}
